import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { AggregatedStatus, InstallationItem, InstallationItemGatewayDevice } from '@vi-iot/client';

import { GTMService } from '@services';
import {
  DeviceTypes,
  InstallationDetailsStore,
  InstallationsStore,
  MapStore,
  selectedKanbanPage,
} from '@store';
import { ClusterIconStyle } from '@angular/google-maps';
import { CustomTranslationsService } from '../../operational-data-common/translations';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

declare interface ClusterIconInfo {
  // added here as it is no longer exported by @angular/google-maps
  index: number;
  text: string;
  title: string;
}

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnInit, OnDestroy {
  readonly MULTIGATEWAY_PLACEHOLDER_IMG_URL = '/assets/img/installation-multigateway.svg';

  clusterStyle: ClusterIconStyle[] = [
    {
      anchorText: [7, 22],
      textColor: 'white',
      height: 56,
      width: 68,
      textSize: 12,
      url: '/assets/icons/map/house-icons/house-indicator.png',
    },
  ];
  aggregatedStatus = AggregatedStatus;
  searchTerm = '';
  coldSystemTooltip: string;
  mapCenter: { lat: number; lng: number };
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public mapStore: MapStore,
    public installationsStore: InstallationsStore,
    private gtmService: GTMService,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private customTranslationsService: CustomTranslationsService
  ) {}

  ngOnInit(): void {
    this.loadInstallations();
    this.coldSystemTooltip = this.translate.instant(
      this.customTranslationsService.INSTALLATION_STATUS_COLD_SYSTEM
    );
    this.mapStore.mapCenter$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((center) => {
      if (center && center.lat !== null && center.lng !== null) {
        this.mapCenter = center;
      }
    });
  }

  ngOnDestroy(): void {
    this.mapStore.unselectInstallation();
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  calculateFunction(
    markers: google.maps.Marker[],
    _clusterIconStylesCount: number
  ): ClusterIconInfo {
    return {
      title: `${markers.length}`,
      text: `${markers.length}`,
      index: markers.length,
    };
  }

  trackByIndex(index: number): number {
    return index;
  }

  getIconUrl(installation: InstallationItem, selected?: InstallationItem[]): string {
    // TODO - selected is actually not used at all - check later
    const isInstallationMultigateway = installation.gateways.length > 1;
    if (isInstallationMultigateway && selected) {
      return '/assets/icons/map/house-icons/house-selected.png';
    }
    if (isInstallationMultigateway && !selected) {
      return '/assets/icons/map/house-icons/house-unselected.png';
    }
    const status = installation.gateways?.[0]?.aggregatedStatus.toLowerCase();
    if (selected && selected.length > 1) {
      return '/assets/icons/map/house-icons/house-indicator.png';
    } else if (selected && selected.some((item: InstallationItem) => item === installation)) {
      if (status) {
        return '/assets/icons/map/house-icons/house-selected-' + status + '.png';
      } else {
        return '/assets/icons/map/house-icons/house-selected.png';
      }
    } else {
      if (status) {
        return '/assets/icons/map/house-icons/house-unselected-' + status + '.png';
      } else {
        return '/assets/icons/map/house-icons/house-unselected.png';
      }
    }
  }

  getInstallationTitle(
    installation: InstallationItem,
    mainDevices: InstallationItemGatewayDevice[]
  ): string {
    if (installation.gateways.length > 1) {
      return (
        this.translate.instant('+MAP.MULTIGATEWAY_HEADER_LABEL') + ' ' + installation?.ownerName
      );
    }

    return (
      mainDevices?.[0]?.marketingName ||
      this.translate.instant('+INSTALLATIONS.INSTALLATIONS.INSTALLATION_OF') +
        ' ' +
        installation?.ownerName
    );
  }

  getImageUri(
    installation: InstallationItem,
    mainDevices: InstallationItemGatewayDevice[]
  ): string {
    if (installation.gateways.length > 1) {
      return this.MULTIGATEWAY_PLACEHOLDER_IMG_URL;
    }

    const gatewayDevices = installation.gateways?.[0]?.devices;
    if (gatewayDevices && this.isStandaloneGateway(gatewayDevices)) {
      return '/assets/img/devices/standalone.png';
    }

    return mainDevices?.[0]?.imageUrl || '';
  }

  selectInstallation(installation: InstallationItem): void {
    this.mapStore.selectInstallation(installation);

    this.gtmService.track('onMapInstallationSelect');
  }

  private loadInstallations(): void {
    const installationId = +this.activatedRoute.snapshot.queryParamMap.get('id');

    if (installationId) {
      this.mapStore.loadInstallation(installationId);
    } else {
      this.mapStore.loadAllInstallations();
    }
  }

  private isStandaloneGateway(devices: InstallationItemGatewayDevice[]): boolean {
    const isRoomControlDevice = devices.some((dev) => dev.deviceType === DeviceTypes.RoomControl);
    const isHeatingDevice = devices.some((dev) => dev.deviceType === DeviceTypes.Heating);

    return isRoomControlDevice && !isHeatingDevice;
  }

  onListViewSelection(link: string): void {
    localStorage.setItem(selectedKanbanPage, link);
  }
}
